import React, { forwardRef } from 'react'

import { FetchResult } from '@apollo/client'
import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import ReactSelect, { ActionMeta, GroupBase, Props as SelectProps, SingleValue } from 'react-select'
import { SpaceProps, WidthProps } from 'styled-system'

import { Select } from '@/atoms/input'
import { SelectOption } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: string
  fieldProps?: SpaceProps & WidthProps
  placeholder?: string
  name?: string
  onChange?: (option: SelectOption, actionMeta?: ActionMeta<SelectOption>) => void | Promise<void | FetchResult>
  hideErrorMessage?: boolean
  menuHeight?: number
  valueHeight?: number
  tooltip?: string
}

export type SelectFieldProps = OwnProps & SelectProps<SelectOption, boolean, GroupBase<SelectOption>>

const SelectField = forwardRef<React.ElementRef<typeof ReactSelect>, SelectFieldProps>(
  (
    { menuHeight, valueHeight, label, options, fieldProps, placeholder, name, onChange, defaultValue, tooltip, hideErrorMessage, ...rest },
    ref,
  ) => {
    const { t } = useTranslation()
    const [field, meta, helpers] = useField(name)
    const hasError: boolean = meta.touched && !!meta.error
    const selectedValue = options?.find((option: SelectOption) => JSON.stringify(option.value) === JSON.stringify(field.value)) ?? null

    const handleOnChange = (option: SelectOption, actionMeta?: ActionMeta<SelectOption>): void => {
      if (actionMeta && actionMeta.action === 'clear') {
        helpers.setValue && helpers.setValue('')
        onChange && onChange(option, actionMeta)

        return
      }
      onChange && onChange(option, actionMeta)
      helpers.setValue && helpers.setValue(option.value)

      return
    }

    return (
      <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
        <Select
          ref={ref}
          options={options}
          hasError={hasError}
          value={selectedValue as SingleValue<SelectOption>}
          name={field.name}
          onChange={handleOnChange}
          placeholder={placeholder || t('global.placeholder.select')}
          menuHeight={menuHeight}
          valueHeight={valueHeight}
          {...rest}
        />
      </FieldWrapper>
    )
  },
)

SelectField.displayName = 'SelectField'

export default SelectField
